import { useAnim, useSEO } from "../hooks.jsx";
import { Link } from "react-router-dom";
import { GUIDES, PAGE_SEO, SITE_URL, breadcrumb } from "../data.js";
import CtaBand from "../components/CtaBand.jsx";
import Breadcrumb from "../components/Breadcrumb.jsx";

const BC_ITEMS = [{ name: "Gestionale noleggio con CaRGOS", path: "/gestionale-noleggio-cargos-polizia" }];

const COSA_E = [
  {
    t: "Cos'è CaRGOS",
    d: "Ca.R.G.O.S. (Car Renter Guardian Operation System) è il servizio della Polizia di Stato a cui chi noleggia veicoli deve comunicare i contratti di noleggio, ai sensi dell'art. 17 del D.L. 113/2018. La comunicazione avviene per via telematica tramite il portale cargos.poliziadistato.it.",
  },
  {
    t: "Perché a mano è un problema",
    d: "Ogni contratto va trasmesso in un formato ministeriale a lunghezza fissa (il tracciato CEN, un record da 1505 caratteri) con dati del cliente, della patente, del documento e del veicolo. Ricopiarli a mano dal contratto al portale significa doppio lavoro e più possibilità di errore.",
  },
];

const COME_FUNZIONA = [
  {
    t: "Inserisci le credenziali una volta sola",
    d: "Dal pannello impostazioni inserisci username e password rilasciati dalla Questura e l'ApiKey generata nella sezione \"Sicurezza\" del portale CaRGOS. Le credenziali sono salvate lato server e non sono mai leggibili dal browser.",
  },
  {
    t: "Le verifichi subito",
    d: "Con \"Testa Connessione\" il sistema contatta il server ufficiale della Polizia di Stato e controlla che credenziali e ApiKey siano accettate, prima ancora di salvarle. Se qualcosa non torna, lo vedi subito e non a ridosso della prima consegna.",
  },
  {
    t: "Alla consegna, l'invio parte da solo",
    d: "Quando confermi la consegna di un veicolo a noleggio, il gestionale genera il record ministeriale e lo trasmette in tempo reale. Non c'è un secondo passaggio da ricordarsi.",
  },
];

const PUNTI = [
  "Nessuna doppia digitazione: i dati arrivano dalla prenotazione, dall'anagrafica del cliente e dalla scheda del veicolo",
  "Controllo dei campi obbligatori prima dell'invio, per intercettare i dati mancanti prima che lo faccia il portale",
  "Credenziali verificate contro il servizio reale della Polizia di Stato, non solo controllate nel formato",
  "Stesso pannello per prenotazioni, contratti PDF e comunicazione alla Questura",
];

export default function GestionaleNoleggioCargos({ go }) {
  useAnim();
  useSEO({
    ...PAGE_SEO["gestionale-noleggio-cargos-polizia"],
    schema: [
      breadcrumb(BC_ITEMS),
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "Gestionale per il noleggio auto con CaRGOS e Polizia di Stato",
        "inLanguage": "it",
        "mainEntityOfPage": `${SITE_URL}/gestionale-noleggio-cargos-polizia`,
        "publisher": { "@type": "Organization", "name": "NoleggioinCloud", "url": SITE_URL },
      },
    ],
  });

  return (
    <>
      <Breadcrumb items={BC_ITEMS} />
      <section className="ph">
        <div className="ph-in an">
          <div className="chip">Guida</div>
          <h1 className="h2" style={{ fontSize: "clamp(1.9rem,4vw,2.9rem)" }}>
            Gestionale per noleggio con <mark>CaRGOS</mark> e Polizia di Stato
          </h1>
          <p className="lead" style={{ marginTop: ".65rem" }}>
            La comunicazione dei contratti alla Questura non dovrebbe essere un
            lavoro a parte. Ecco cos'è CaRGOS e come farla partire in automatico
            dal gestionale.
          </p>
        </div>
      </section>

      <section className="sec" style={{ background: "var(--bg)" }}>
        <div className="wrap" style={{ maxWidth: 820 }}>
          {COSA_E.map((d) => (
            <div key={d.t} className="an" style={{ marginBottom: "1.75rem" }}>
              <h2 style={{ fontSize: "1.15rem", marginBottom: ".4rem" }}>{d.t}</h2>
              <p style={{ fontSize: ".95rem", color: "var(--muted)", lineHeight: 1.75 }}>{d.d}</p>
            </div>
          ))}

          <h2 className="an" style={{ fontSize: "1.3rem", margin: "2.25rem 0 1rem" }}>
            Come funziona in NoleggioinCloud
          </h2>
          {COME_FUNZIONA.map((d, i) => (
            <div key={d.t} className="an" style={{ marginBottom: "1.5rem" }}>
              <h3 style={{ fontSize: "1.05rem", marginBottom: ".4rem" }}>{i + 1}. {d.t}</h3>
              <p style={{ fontSize: ".95rem", color: "var(--muted)", lineHeight: 1.75 }}>{d.d}</p>
            </div>
          ))}

          <div className="an" style={{ marginTop: "2.5rem", padding: "1.5rem", borderRadius: 14, background: "var(--bg2, #f6f8fc)" }}>
            <h2 style={{ fontSize: "1.15rem", marginBottom: ".6rem" }}>Cosa ti risparmia</h2>
            <ul style={{ paddingLeft: "1.1rem", color: "var(--muted)", fontSize: ".95rem", lineHeight: 1.85 }}>
              {PUNTI.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>

          <p className="an" style={{ marginTop: "1.75rem", fontSize: ".85rem", color: "var(--muted)", lineHeight: 1.7 }}>
            Questa pagina è informativa e non sostituisce una consulenza legale:
            per sapere se e come l'obbligo si applica alla tua attività, fai
            riferimento alla normativa vigente e alla tua Questura.
          </p>

          <div className="an" style={{ marginTop: "2rem" }}>
            <h2 style={{ fontSize: "1.05rem", marginBottom: ".5rem" }}>Approfondisci</h2>
            <ul style={{ paddingLeft: "1.1rem", color: "var(--muted)", fontSize: ".95rem", lineHeight: 2 }}>
              <li><Link to="/funzionalita">Tutte le funzionalità</Link></li>
              {GUIDES.filter((g) => g.slug !== "gestionale-noleggio-cargos-polizia").map((g) => (
                <li key={g.slug}><Link to={`/${g.slug}`}>{g.label}</Link></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CtaBand go={go} />
    </>
  );
}
